import {
  DateRangePickerButton,
  FilterOptionButton,
} from '@/components/common/FilterButtons';

import { Input } from '@/components/common/Input';
import { format } from 'date-fns';
import { useEffect, useRef, useState } from 'react';

export type QrCodeCategory = 'doctors' | 'clinics' | 'custom';

export interface AppliedQrCodeFilters {
  category: QrCodeCategory;
  name: string;
  dateFrom: string;
  dateTo: string;
}

const CATEGORY_LABELS: Record<QrCodeCategory, string> = {
  doctors: 'Doctors',
  clinics: 'Clinics',
  custom: 'Custom',
};

interface ManageQrCodesFiltersProps {
  isDoctor: boolean;
  onApply: (filters: AppliedQrCodeFilters) => void;
}

export function ManageQrCodesFilters({
  isDoctor,
  onApply,
}: ManageQrCodesFiltersProps) {
  const [activeCategory, setActiveCategory] =
    useState<QrCodeCategory>('doctors');
  const [nameFilter, setNameFilter] = useState('');
  const [dateFrom, setDateFrom] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(
    format(
      new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1),
      'yyyy-MM-dd',
    ),
  );
  const [isDateActive, setIsDateActive] = useState(false);
  const filtersRef = useRef({ activeCategory, dateFrom, dateTo, isDateActive });
  filtersRef.current = { activeCategory, dateFrom, dateTo, isDateActive };

  useEffect(() => {
    const timer = setTimeout(() => {
      const f = filtersRef.current;
      onApply({
        category: f.activeCategory,
        name: nameFilter,
        dateFrom: f.isDateActive ? f.dateFrom : '',
        dateTo: f.isDateActive ? f.dateTo : '',
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [nameFilter]);

  const applyFilters = (
    overrides?: Partial<{
      category: QrCodeCategory;
      dateFrom: string;
      dateTo: string;
      isDateActive: boolean;
    }>,
  ) => {
    const cat = overrides?.category ?? activeCategory;
    const df = overrides?.dateFrom ?? dateFrom;
    const dt = overrides?.dateTo ?? dateTo;
    const da = overrides?.isDateActive ?? isDateActive;
    onApply({
      category: cat,
      name: nameFilter,
      dateFrom: da ? df : '',
      dateTo: da ? dt : '',
    });
  };

  const handleCategoryChange = (category: QrCodeCategory) => {
    setActiveCategory(category);
    applyFilters({ category });
  };

  const handleDateRangeChange = (from: string, to: string) => {
    setDateFrom(from);
    setDateTo(to);
    setIsDateActive(true);
    applyFilters({ dateFrom: from, dateTo: to, isDateActive: true });
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      applyFilters();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {(Object.keys(CATEGORY_LABELS) as Array<QrCodeCategory>)
          .filter((cat) => !isDoctor || cat !== 'doctors')
          .map((category) => (
            <FilterOptionButton
              key={category}
              label={CATEGORY_LABELS[category]}
              isActive={activeCategory === category}
              onClick={() => handleCategoryChange(category)}
            />
          ))}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-full max-w-56">
          <Input
            label="Filter by name"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            onKeyDown={handleNameKeyDown}
          />
        </div>

        <DateRangePickerButton
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateRangeChange={handleDateRangeChange}
          isActive={isDateActive}
        />
      </div>
    </div>
  );
}
